import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import ColdIcon from "../../Icons/ColdIcon";
import HotIcon from "../../Icons/HotIcon";
import { useMutation, } from "@tanstack/react-query";
import { useState, useRef, useEffect, type CSSProperties } from "react";
import { FreezeStudent } from "../../../api/Students/FreezeStudent";
import { updateExpiryDate } from "../../../api/Students/UpdateExpiryDate";
import { queryClient } from "../../../main";
import { formatSubscriptionExpiry } from "../../../utils/utils";
import { CalendarDays, Check } from "lucide-react";
const StudentsFreezeRow = ({
  fullName,
  email,
  phone,
  country,
  studentId,
  courseId,
  frozen,
  subscriptionEndsAt,
}: {
  fullName: string;
  email: string;
  phone: string;
  country: string;
  studentId: string;
  courseId: string;
  frozen: boolean;
  subscriptionEndsAt: number;
}) => {
  const { mutate: mutateStatus } = useMutation({
    mutationFn: ({
      isFreeze,
      studentId,
      courseId,
    }: {
      isFreeze: boolean;
      studentId: string;
      courseId: string;
    }) => FreezeStudent({ isFreeze }, studentId, courseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["studentsbycourse"] });
      toast.success("Student Status Changed Successfully");
    },
    onError: (error) => {
      toast.error("Failed to update student status");
      console.error(error);
    },
  });

  const toInputDate = (ts: number) =>
    ts ? new Date(ts).toISOString().split("T")[0] : "";

  const [expiryDate, setExpiryDate] = useState(toInputDate(subscriptionEndsAt));
  const [showPicker, setShowPicker] = useState(false);
  const [popupStyle, setPopupStyle] = useState<CSSProperties>({});
  const pickerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowPicker(false);
      }
    };
    if (showPicker) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPicker]);

  const { mutate: mutateExpiry, isPending: isExpiryPending } = useMutation({
    mutationFn: () =>
      updateExpiryDate(studentId, courseId, new Date(expiryDate).toISOString()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["studentsbycourse"] });
      toast.success("Expiry date updated successfully");
      setShowPicker(false);
    },
    onError: (error) => {
      toast.error("Failed to update expiry date");
      console.error(error);
    },
  });

  const handleChangeStatus = () => {
    mutateStatus({
      isFreeze: !frozen,
      studentId: studentId,
      courseId: courseId,
    });
  };

  const updatePickerPosition = () => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (viewportWidth < 640) {
      setPopupStyle({
        position: "fixed",
        left: 12,
        right: 12,
        bottom: 16,
        zIndex: 9999,
      });
      return;
    }

    const panelWidth = 256;
    const panelHeight = 150;
    const safeGap = 12;
    const left = Math.min(
      Math.max(safeGap, rect.left),
      viewportWidth - panelWidth - safeGap
    );
    const preferredTop = rect.bottom + 8;
    const top =
      preferredTop + panelHeight > viewportHeight
        ? Math.max(safeGap, rect.top - panelHeight - 8)
        : preferredTop;

    setPopupStyle({
      position: "fixed",
      top,
      left,
      zIndex: 9999,
    });
  };

  return (
    <>
      <tr>
        <Link className="w-full" to={`/dashboard/students/${studentId}`}>
          <td className=" px-2 first:pl-5 last:pr-5  py-3 whitespace-nowrap">
            <div className="rounded-full text-left py-0.5 text-sm font-semibold flex items-center gap-4">
              {fullName}
            </div>
          </td>
        </Link>
        <td className=" px-2 first:pl-5 last:pr-5  py-3 whitespace-nowrap">
          <div className="inline-flex rounded-full text-left py-0.5 text-sm font-semibold">
            {email}
          </div>
        </td>
        <td className=" px-2 first:pl-5 last:pr-5  py-3 whitespace-nowrap">
          <div className="inline-flex rounded-full text-left py-0.5 text-sm font-semibold">
            {phone}
          </div>
        </td>
        <td className=" px-2 first:pl-5 last:pr-5  py-3 whitespace-nowrap">
          <div className="inline-flex rounded-full text-left py-0.5 text-sm font-semibold">
            {country}
          </div>
        </td>
        <td className=" px-2 first:pl-5 last:pr-5  py-3 whitespace-nowrap">
          <div
            className={`inline-flex uppercase rounded-full text-left py-0.5 text-sm font-semibold ${frozen ? "text-blue-500" : "text-red-500"
              }`}
          >
            {frozen ? "Frozen" : "Unfrozen"}
          </div>
        </td>
        <td className=" px-2 first:pl-5 last:pr-5  py-3 whitespace-nowrap">
          <div className="inline-flex rounded-full text-left py-0.5 text-sm font-semibold">
            {formatSubscriptionExpiry(subscriptionEndsAt)}
          </div>
        </td>
        <td className="px-2 first:pl-5 last:pr-5  py-3 whitespace-nowrap">
          <div className="flex rounded-xl justify-center items-center bg-[#FAFBFD] border-[#D5D5D5] border py-3 text-sm font-semibold">
            <button onClick={handleChangeStatus}>
              {frozen ? <HotIcon /> : <ColdIcon />}
            </button>
          </div>
        </td>
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
          <div ref={pickerRef}>
            <button
              ref={buttonRef}
              onClick={() => {
                if (!showPicker) {
                  updatePickerPosition();
                }
                setShowPicker((v) => !v);
              }}
              className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-sm font-bold text-white shadow-[0_10px_24px_rgba(254,87,34,0.18)] transition hover:bg-[#a32c07]"
            >
              <CalendarDays size={14} />
              Set Date
            </button>
            {showPicker && (
              <div
                style={popupStyle}
                className="flex w-auto flex-col gap-3 rounded-2xl border border-black/10 bg-white p-4 shadow-[0_24px_70px_rgba(17,24,22,0.18)] sm:w-64"
              >
                <input
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  className="h-10 rounded-xl border border-black/10 bg-dashboardBg px-3 text-sm font-semibold text-textPrimary outline-none transition focus:border-secondary focus:bg-white focus:ring-4 focus:ring-secondary/10"
                />
                <button
                  onClick={() => mutateExpiry()}
                  disabled={!expiryDate || isExpiryPending}
                  className="inline-flex min-h-10 items-center justify-center gap-1.5 rounded-full bg-secondary px-4 text-sm font-bold text-white transition hover:bg-[#a32c07] disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  <Check size={14} />
                  {isExpiryPending ? "Saving..." : "Confirm"}
                </button>
              </div>
            )}
          </div>
        </td>
      </tr>
    </>
  );
};

export default StudentsFreezeRow;
